import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Flame, Loader2, Lock, Navigation, ShieldCheck } from "lucide-react";
import SearchLocationFields from "@/components/SearchLocationFields";
import FilterField from "@/components/ui/FilterField";
import { MultiSelect } from "@/components/ui/MultiSelect";
import type { Option } from "@/components/ui/MultiSelect";
import type { LocationValue } from "@/lib/locations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { slugify } from "@/lib/geo";
import {
  AGE_VERIFIED_KEY,
  isAgeVerified,
  isOnboardingComplete,
  markOnboardingComplete,
  writeFlag,
} from "@/lib/onboarding";
import { loadUserPrefs, storeUserPrefs } from "@/lib/preferences";
import useUserLocation from "@/hooks/useUserLocation";

/**
 * Verificação obrigatória de idade. Depois da confirmação, abre um modal
 * opcional para filtros iniciais; fechar o modal mantém a navegação normal.
 */
interface CategoryOption {
  name: string;
  slug: string;
}

interface AttributeCategory {
  id: string;
  name: string;
  options: Option[];
}

interface FilterState {
  category: string;
  state: string;
  city: string;
  neighborhood: string;
  attributes: Record<string, string[]>;
}

export default function PrivacyGate() {
  const navigate = useNavigate();
  const [needsGate, setNeedsGate] = useState(false);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [attributeCategories, setAttributeCategories] = useState<AttributeCategory[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");
  const { loading: geoLoading, requestLocation } = useUserLocation();
  const location = { state, city, neighborhood };

  useEffect(() => {
    if (isAgeVerified()) {
      if (!isOnboardingComplete()) markOnboardingComplete();
      return;
    }

    setNeedsGate(true);
    const prefs = loadUserPrefs();
    if (prefs) {
      if (prefs.category) setCategory(prefs.category);
      if (prefs.state) setState(prefs.state);
      if (prefs.city) setCity(prefs.city);
      if (prefs.neighborhood) setNeighborhood(prefs.neighborhood);
      if (prefs.attributes) setSelectedAttributes(prefs.attributes);
    }
  }, []);

  useEffect(() => {
    if (!showPreferenceModal) return;
    let alive = true;
    api
      .get("/categories")
      .then((r) => alive && setCategories(r.data || []))
      .catch(() => alive && setCategories([]));
    api
      .get("/attributes")
      .then((r) => alive && setAttributeCategories(r.data || []))
      .catch(() => alive && setAttributeCategories([]));
    return () => { alive = false; };
  }, [showPreferenceModal]);

  useEffect(() => {
    if (!needsGate) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [needsGate]);

  useEffect(() => {
    if (!showPreferenceModal) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "";
    return () => { document.body.style.overflow = previous; };
  }, [showPreferenceModal]);

  const buildFilteredUrl = useCallback((filters: FilterState) => {
    const nextCategory = filters.category || "";
    const nextState = filters.state || "";
    const nextCity = filters.city || "";
    const nextNeighborhood = filters.neighborhood || "";

    let path = "/";
    if (nextState) {
      path = `/${nextState.toLowerCase()}`;
      if (nextCity) path += `/${slugify(nextCity)}`;
    }

    const qs = new URLSearchParams();
    if (nextCategory) qs.set("category", nextCategory);
    if (nextNeighborhood) qs.set("neighborhood", nextNeighborhood);
    
    // Add attributes to URL
    if (filters.attributes) {
      Object.entries(filters.attributes).forEach(([id, vals]) => {
        if (vals && vals.length > 0) {
          qs.set(`attr_${id}`, vals.join(","));
        }
      });
    }

    const query = qs.toString();
    return `${path}${query ? `?${query}` : ""}`;
  }, []);

  const applyPreferenceFilters = useCallback((filters: FilterState) => {
    const next = {
      category: filters.category || "",
      state: filters.state || "",
      city: filters.city || "",
      neighborhood: filters.neighborhood || "",
    };

    storeUserPrefs(next);
    setShowPreferenceModal(false);
    markOnboardingComplete();

    const hasAnyFilter = next.category || next.state || next.city || next.neighborhood || 
      (filters.attributes && Object.values(filters.attributes).some(v => v.length > 0));

    if (hasAnyFilter) {
      navigate(buildFilteredUrl({ ...next, attributes: filters.attributes }));
    }
  }, [buildFilteredUrl, navigate]);

  const handleLocationChange = useCallback((next: LocationValue) => {
    setState(next.state || "");
    setCity(next.city || "");
    setNeighborhood(next.neighborhood || "");
    setLocationMessage("");
  }, []);

  const handleLocate = useCallback(async () => {
    setLocationMessage("");
    try {
      const { match } = await requestLocation({ withGeocode: true });
      if (match) {
        const filters = {
          category,
          state: match.state || "",
          city: match.city || "",
          neighborhood: match.neighborhood || "",
        };
        setState(filters.state);
        setCity(filters.city);
        setNeighborhood(filters.neighborhood);
        applyPreferenceFilters({ ...filters, attributes: selectedAttributes });
      }
    } catch (_e) {
      setLocationMessage("Não foi possível obter sua localização. Verifique a permissão do navegador e tente novamente.");
    }
   }, [applyPreferenceFilters, category, requestLocation, selectedAttributes]);

  const handleConfirm = useCallback(() => {
    setSubmitting(true);
    writeFlag(AGE_VERIFIED_KEY, true);
    setNeedsGate(false);
    setShowPreferenceModal(true);
    setSubmitting(false);
  }, []);

  const handleReject = () => {
    window.location.href = "https://www.wikipedia.org";
  };

  const handleApplyFilters = useCallback(() => {
    applyPreferenceFilters({ category, state, city, neighborhood, attributes: selectedAttributes });
  }, [applyPreferenceFilters, category, city, neighborhood, state, selectedAttributes]);

  const handlePreferenceOpenChange = useCallback((open: boolean) => {
    setShowPreferenceModal(open);
    if (!open) {
      setLocationMessage("");
      markOnboardingComplete();
    }
  }, []);

  if (!needsGate && !showPreferenceModal) return null;

  return (
    <>
      {needsGate && (
        <div
          className="fixed inset-0 z-[100] overflow-y-auto bg-cream-50"
          role="region"
          aria-labelledby="privacy-gate-title"
          data-testid="privacy-gate"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(60% 50% at 80% 0%, rgba(225,29,72,0.10), transparent 70%), radial-gradient(45% 35% at 5% 100%, rgba(236,72,153,0.10), transparent 70%)",
            }}
          />

          <main className="relative flex min-h-screen w-full flex-col">
            <header className="px-6 pt-8 sm:px-10 sm:pt-10">
              <div className="flex items-center justify-center gap-2.5">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-heat-grad text-white shadow-heat-glow">
                  <Flame className="h-5 w-5 fill-current" />
                </span>
                <span className="font-display text-2xl font-semibold tracking-tight text-ink-900">
                  forfada<span className="text-heat-500">.</span>
                </span>
              </div>
            </header>

            <div className="flex-1 px-6 py-10 sm:px-10 sm:py-14">
              <div className="mx-auto w-full max-w-2xl">
                <div className="text-center">
                  <span className="inline-flex items-center gap-2 rounded-full bg-heat-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-heat-700 ring-1 ring-heat-200">
                    <Lock className="h-3.5 w-3.5" />
                    Conteúdo 18+
                  </span>
                  <h1
                    id="privacy-gate-title"
                    className="mt-5 font-display text-3xl font-semibold leading-[1.05] tracking-tight text-ink-900 sm:text-4xl lg:text-5xl"
                  >
                    Bem-vindo ao <span className="text-heat-grad">forfada</span>
                    <span className="text-heat-500">.</span>
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink-700 sm:text-lg">
                    Conteúdo direcionado a maiores de 18 anos. Confirme sua
                    idade para continuar.
                  </p>

                  <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[12px]">
                    <span className="inline-flex items-center gap-2 rounded-full bg-cream-100 px-3 py-1.5 text-ink-700 ring-1 ring-cream-200">
                      <ShieldCheck className="h-3.5 w-3.5 text-heat-500" />
                      Sem Dados Pessoais
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-cream-100 px-3 py-1.5 text-ink-700 ring-1 ring-cream-200">
                      <Lock className="h-3.5 w-3.5 text-heat-500" />
                      Tráfego Criptografado
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-cream-100 px-3 py-1.5 text-ink-700 ring-1 ring-cream-200">
                      <Eye className="h-3.5 w-3.5 text-heat-500" />
                      Sem Vergonha
                    </span>
                  </div>
                </div>

                <section className="mt-10 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={submitting}
                    className="btn-primary w-full py-4 text-base disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="privacy-gate-confirm"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Tenho 18+ e quero continuar
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    className="py-2 text-sm font-semibold text-ink-500 transition-colors hover:text-ink-900"
                    data-testid="privacy-gate-reject"
                  >
                    Sair do site
                  </button>
                </section>
              </div>
            </div>

            <footer className="px-6 pb-8 sm:px-10 sm:pb-10">
              <p className="mx-auto max-w-xl text-center text-[11px] leading-relaxed text-ink-500">
                Ao continuar, você confirma ser maior de 18 anos e concorda com
                nossos Termos. Em conformidade com a Lei nº 8.069/1990 (ECA).
              </p>
            </footer>
          </main>
        </div>
      )}

      <Dialog open={showPreferenceModal} onOpenChange={handlePreferenceOpenChange}>
        <DialogContent
          className="w-[calc(100%-2rem)] max-w-3xl overflow-hidden rounded-3xl border-cream-200 bg-white p-0 shadow-soft"
          data-testid="privacy-preferences-modal"
        >
          <div className="p-5 sm:p-6">
            <DialogHeader className="pr-8">
              <DialogTitle className="font-display text-2xl font-semibold text-ink-900 text-balance">
                Entrar com filtros
              </DialogTitle>
              <DialogDescription className="text-sm text-ink-700 text-pretty">
                Escolha uma categoria e uma região para abrir a listagem filtrada.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-4">
              <button
                type="button"
                onClick={handleLocate}
                disabled={geoLoading}
                aria-describedby={locationMessage ? "privacy-location-message" : undefined}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-heat-200 bg-heat-grad-soft px-4 py-3 font-semibold text-heat-700 transition-colors hover:border-heat-300 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="privacy-gate-locate"
              >
                {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                {geoLoading ? "Localizando..." : "Usar minha localização"}
              </button>

              {locationMessage && (
                <p
                  id="privacy-location-message"
                  role="alert"
                  className="rounded-xl border border-heat-200 bg-heat-50 px-3 py-2 text-sm text-heat-800"
                >
                  {locationMessage}
                </p>
              )}

              <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-ink-400">
                <span className="h-px flex-1 bg-cream-200" />
                ou escolha manualmente
                <span className="h-px flex-1 bg-cream-200" />
              </div>

              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-2">
                <FilterField label="Categoria">
                  <MultiSelect
                    options={categories.map((c) => ({ label: c.name, value: c.slug }))}
                    selected={category ? [category] : []}
                    onChange={(vals) => setCategory(vals[0] || "")}
                    multiple={false}
                    placeholder="Todas"
                    className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
                    data-testid="privacy-gate-category"
                  />
                </FilterField>

                <SearchLocationFields
                  value={location}
                  onChange={handleLocationChange}
                  context={{ category }}
                  variant="listing"
                />

                {attributeCategories.map((cat) => {
                  const selected = selectedAttributes[cat.id] || [];
                  return (
                    <FilterField key={cat.id} label={cat.name} className="sm:w-auto sm:min-w-[200px]">
                      <MultiSelect
                        options={cat.options}
                        selected={selected}
                        onChange={(vals) => setSelectedAttributes(p => ({ ...p, [cat.id]: vals }))}
                        placeholder="Todos"
                        className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
                      />
                    </FilterField>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => handlePreferenceOpenChange(false)}
                className="btn-outline px-5 py-3"
                data-testid="privacy-preferences-skip"
              >
                Agora não
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className="btn-primary px-5 py-3"
                data-testid="privacy-preferences-apply"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
